import React, { Fragment, useState } from 'react';
import { useController } from 'react-hook-form';
import { Button, Input, Input as InputAntd, InputNumber as InputNumberAntD, Modal } from 'antd';
import { Required, RowComponent } from '../../../styles/global-style';
import { TextXSMall, TextInputLabel } from '../../text';
import { renderTypeError } from '../index.jsx';
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  QrcodeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { QrReader } from 'react-qr-reader';
import { useDispatch } from 'react-redux';
import { setStoreDetails } from '../../../redux/storeQr/action';
import { setSearchClaim } from '../../../redux/search/action';
import { setScanClaim } from '../../../redux/scan/action';
import { setSearchCode } from '../../../redux/searchCode/action';
import { setScanCode } from '../../../redux/scanCode/action';

const InputLabel = ({ control, item, handleChange }) => {
  const {
    rules,
    name,
    defaultValue,
    label,
    placeholder,
    disabled,
    onEnter,
    inputType = 'text',
    inputStep = '0.01',
    dataCy,
    ...propsInput
  } = item;

  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      const { value: v } = event.target;
      onEnter && onEnter(v, true);
    }
  };

  return (
    <Fragment key={name}>
      <RowComponent>
        {label && <TextInputLabel text={label} />}
        {rules && rules.required && <Required>*</Required>}
      </RowComponent>
      <InputAntd
        id={name}
        name={name}
        value={value}
        type={inputType}
        step={inputStep}
        disabled={disabled}
        onChange={(e) => handleChange(e, item)}
        onKeyDown={(e) => handleKeyDown(e)}
        placeholder={placeholder}
        autoComplete={'off'}
        {...dataCy}
        {...propsInput}
      />
      {error && <TextXSMall text={renderTypeError(item, error)} color="red" />}
    </Fragment>
  );
};

export const InputLabelQr = ({ control, item, handleChange }) => {
  const {
    rules,
    name,
    defaultValue,
    label,
    placeholder,
    disabled,
    onEnter,
    inputType = 'text',
    inputStep = '0.01',
    dataCy,
    ...propsInput
  } = item;

  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  const [isQrScannerVisible, setQrScannerVisible] = useState(false);
  const dispatch = useDispatch();

  const handleScan = (result) => {
    if (result?.text) {
      try {
        if (result.text.startsWith('{')) {
          const storeData = JSON.parse(result.text);
          const { store_id, store_name, store_master_id, store_master_name, store_no } = storeData;

          field.onChange(store_id);

          dispatch(
            setStoreDetails({
              store_id,
              store_name,
              store_master_id,
              store_master_name,
              store_no,
            }),
          );

          if (handleChange) {
            const syntheticEvent = {
              target: {
                name,
                value: store_id,
              },
            };
            handleChange(syntheticEvent, item);
          }
          setQrScannerVisible(false);
        }
      } catch (error) {}
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && onEnter) {
      onEnter(event.target.value, true);
    }
  };

  return (
    <Fragment key={name}>
      <RowComponent>
        {label && <TextInputLabel text={label} />}
        {rules?.required && <Required>*</Required>}
      </RowComponent>

      <div className="flex w-full">
        <Input
          id={name}
          name={name}
          value={value}
          type={inputType}
          step={inputStep}
          disabled={true}
          onChange={(e) => handleChange(e, item)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="flex-grow"
          style={{ width: 'calc(100% - 40px)' }}
          {...dataCy}
          {...propsInput}
        />
        <Button
          icon={<QrcodeOutlined />}
          onClick={() => setQrScannerVisible(true)}
          className="ml-2"
          style={{ width: '40px', top: '1px' }}
        />
      </div>

      {error && <TextXSMall text={renderTypeError(item, error)} color="red" />}

      <Modal
        title="สแกน QR Code"
        open={isQrScannerVisible}
        onCancel={() => setQrScannerVisible(false)}
        footer={null}
        width={400}
        destroyOnClose={true}
      >
        {isQrScannerVisible && (
          <div className="relative w-full">
            <QrReader
              onResult={handleScan}
              constraints={{
                facingMode: 'environment',
              }}
              className="w-full"
              videoStyle={{ width: '100%' }}
            />
            <div
              className="absolute inset-0 border-2 border-blue-500 rounded-lg pointer-events-none"
              style={{
                margin: '20%',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              }}
            />
          </div>
        )}
      </Modal>
    </Fragment>
  );
};

export const InputQRCode = ({ control, item, handleChange }) => {
  const {
    rules,
    name,
    defaultValue,
    label,
    placeholder,
    onScanSuccess,
    disabled,
    onEnter,
    inputType = 'text',
    inputStep = '0.01',
    dataCy,
    ...propsInput
  } = item;

  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  const [isQrScannerVisible, setQrScannerVisible] = useState(false);

  const handleScan = (result) => {
    if (!result?.text) return;
    handleChange(result?.text, item);
    setQrScannerVisible(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && onEnter) {
      onEnter(event.target.value, true);
    }
  };

  return (
    <Fragment key={name}>
      <RowComponent>
        {label && <TextInputLabel text={label} />}
        {rules?.required && <Required>*</Required>}
      </RowComponent>

      <div className="flex w-full">
        <Input
          id={name}
          name={name}
          value={value}
          type={inputType}
          step={inputStep}
          disabled={false}
          onChange={(e) => handleChange(e, item)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="flex-grow"
          style={{ width: 'calc(100% - 40px)' }}
          {...dataCy}
          {...propsInput}
        />
        <Button
          icon={<QrcodeOutlined />}
          onClick={() => setQrScannerVisible(true)}
          className="ml-2"
          style={{ width: '40px', top: '1px' }}
        />
      </div>

      {error && <TextXSMall text={renderTypeError(item, error)} color="red" />}

      {isQrScannerVisible && (
        <Modal
          title="สแกน QR Code"
          open={isQrScannerVisible}
          onCancel={() => setQrScannerVisible(false)}
          footer={null}
          width={400}
        >
          <div className="relative w-full">
            <QrReader
              onResult={handleScan}
              constraints={{
                facingMode: 'environment',
              }}
              className="w-full"
              videoStyle={{ width: '100%' }}
            />
            <div
              className="absolute inset-0 border-2 border-blue-500 rounded-lg pointer-events-none"
              style={{
                margin: '20%',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              }}
            />
          </div>
        </Modal>
      )}
    </Fragment>
  );
};

export const InputQRCodeTwo = ({ control, item, handleChange }) => {
  const {
    rules,
    name,
    defaultValue,
    label,
    placeholder,
    onScanSuccess,
    disabled,
    onEnter,
    inputType = 'text',
    inputStep = '0.01',
    dataCy,
    ...propsInput
  } = item;

  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  const dispatch = useDispatch();
  const [isQrScannerVisible, setQrScannerVisible] = useState(false);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && onEnter) {
      onEnter(event.target.value, true);
    }
  };

  const handleSearch = (result) => {
    switch (name) {
      case 'warrantyNumberCode':
        dispatch(setSearchCode({ searchCode: result }));
        break;
      case 'claimCode':
        dispatch(setSearchClaim({ searchClaim: result }));
        break;
      default:
        break;
    }
  };

  const handleScan = (result) => {
    if (result?.text) {
      switch (name) {
        case 'warrantyNumberCode':
          dispatch(setScanCode({ scanCode: result }));
          break;
        case 'claimCode':
          dispatch(setScanClaim({ scanClaim: result }));
          break;
      }
      setQrScannerVisible(false);
    }
  };

  return (
    <Fragment key={name}>
      <RowComponent>
        {label && <TextInputLabel text={label} />}
        {rules?.required && <Required>*</Required>}
      </RowComponent>

      <div className="flex w-full">
        <InputAntd
          id={name}
          addonAfter={<SearchOutlined onClick={() => handleSearch(value)} />}
          name={name}
          value={value}
          type={inputType}
          step={inputStep}
          disabled={false}
          onChange={(e) => handleChange(e, item)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="flex-grow"
          style={{ width: 'calc(100% - 50px)' }}
          {...dataCy}
          {...propsInput}
        />
        <Button
          icon={<QrcodeOutlined />}
          onClick={() => setQrScannerVisible(true)}
          className="ml-1"
          style={{ width: '40px', top: '1px' }}
        />
      </div>

      {error && <TextXSMall text={renderTypeError(item, error)} color="red" />}

      {isQrScannerVisible && (
        <Modal
          title="สแกน QR Code"
          open={isQrScannerVisible}
          onCancel={() => setQrScannerVisible(false)}
          footer={null}
          width={400}
        >
          <div className="relative w-full">
            <QrReader
              onResult={handleScan}
              constraints={{
                facingMode: 'environment',
              }}
              className="w-full"
              videoStyle={{ width: '100%' }}
            />
            <div
              className="absolute inset-0 border-2 border-blue-500 rounded-lg pointer-events-none"
              style={{
                margin: '20%',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              }}
            />
          </div>
        </Modal>
      )}
    </Fragment>
  );
};

export const InputArea = ({ control, item, handleChange }) => {
  const {
    rules,
    name,
    defaultValue,
    label,
    placeholder,
    disabled,
    onEnter,
    inputType = 'text',
    inputStep = '0.01',
    dataCy,
    ...propsInput
  } = item;

  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      const { value: v } = event.target;
      onEnter && onEnter(v, true);
    }
  };

  return (
    <Fragment key={name}>
      <RowComponent>
        {label && <TextInputLabel text={label} />}
        {rules && rules.required && <Required>*</Required>}
      </RowComponent>
      <InputAntd.TextArea
        id={name}
        name={name}
        value={value}
        type={inputType}
        step={inputStep}
        disabled={disabled}
        onChange={(e) => handleChange(e, item)}
        onKeyDown={(e) => handleKeyDown(e)}
        placeholder={placeholder}
        autoComplete={'off'}
        {...dataCy}
        {...propsInput}
      />
      {error && <TextXSMall text={renderTypeError(item, error)} color="red" />}
    </Fragment>
  );
};

export const InputNumber = ({ control, item, handleChange }) => {
  const {
    rules,
    name,
    defaultValue,
    label,
    placeholder,
    disabled,
    onEnter,
    inputPrecision = 2,
    dataCy,
    ...propsInput
  } = item;

  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      const { value: v } = event.target;
      onEnter && onEnter(v, true);
    }
  };

  return (
    <Fragment key={name}>
      <RowComponent>
        {label && <TextInputLabel text={label} />}
        {rules && rules.required && <Required>*</Required>}
      </RowComponent>
      <InputNumberAntD
        style={{ width: '100%', textAlign: 'right', ...item.style }}
        id={name}
        name={name}
        value={value}
        precision={inputPrecision}
        disabled={disabled}
        onChange={(e) => handleChange(e, item)}
        onKeyDown={(e) => handleKeyDown(e)}
        placeholder={placeholder}
        {...dataCy}
        {...propsInput}
      />
      {error && <TextXSMall text={renderTypeError(item, error)} color="red" />}
    </Fragment>
  );
};

export const MemoizedInputLabel = InputLabel; //memo(, compareRender);

const InputPassword = ({ control, item, handleChange }) => {
  const {
    rules,
    name,
    defaultValue,
    label,
    placeholder,
    disabled,
    onEnter,
    dataCy,
    ...propsInput
  } = item;

  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      const { value: v } = event.target;
      onEnter && onEnter(v, true);
    }
  };

  return (
    <Fragment>
      <RowComponent>
        {label && <TextXSMall text={label} />}
        {rules && rules.required && <Required>*</Required>}
      </RowComponent>
      <InputAntd.Password
        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
        id={name}
        name={name}
        value={value}
        disabled={disabled}
        onChange={(e) => handleChange(e, item)}
        onKeyDown={(e) => handleKeyDown(e)}
        placeholder={placeholder}
        autoComplete={'off'}
        {...dataCy}
        {...propsInput}
      />
      {error && <TextXSMall text={renderTypeError(item, error)} color="red" />}
    </Fragment>
  );
};

export const MemoizedInputPassword = InputPassword;
